import React, { useCallback, useState } from 'react';
import { queryCache } from 'react-query';
import { GradientTextAction, InputLabel, Poppins } from 'src/common';
import Button from 'src/components/form/Button';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { EventCsvHeaders } from 'src/api/types/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Dropzone, DzoneWrap, ErrorItem, ErrorLog } from 'src/common/dropzone';
import {
  EventInput,
  EventCompletionStatus,
  EventKind,
  ObjectKind,
  useUpsertEventMutation,
} from 'src/graphql/_generated';
import { convertDateToISO, delimiters, ParseResult, validateRow, ValidationErrorItem } from 'src/utils/csv';
import { useCache } from 'src/graphql';
import _ from 'lodash';
import { EventCSV, generateCsvTemplateLink, pathNames } from './util';
import { validationSchema } from '../../util';
import { Alert } from '@mui/material';
import CSelect from 'src/components/form/Select';
import { eventCompletionStatusOptions } from 'src/api/types/options';

interface FromCSVProps {
  onClose: () => void;
  setIsCsvDirty: (isDirty: boolean) => void;
  objectType: ObjectKind;
  objectId: string;
  state: {
    file: File | null;
    setFile: (file: File | null) => void;
    readyForUpload: boolean;
    setReadyForUpload: (ready: boolean) => void;
  };
  eventIds: string[];
}

export const FromCSV: React.FC<FromCSVProps> = ({ onClose, setIsCsvDirty, objectType, objectId, state, eventIds }) => {
  const { usersOptios, workspaceId } = useAuth();
  const [error, setError] = useState('');
  const [isUploadSuccessful, setIsUploadSuccessful] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [validEvents, setValidEvents] = useState<EventInput[]>([]);
  const { refetchQuery } = useCache();
  const [hasExistingEvents, setHasExistingEvents] = useState(false);

  const { file, setFile, readyForUpload, setReadyForUpload } = state;

  const isButtonDisabled = !file || (validEvents.length < totalEvents && validEvents.length > 0);
  const csvTemplateLink = generateCsvTemplateLink();

  const [upsertEvent, { loading }] = useUpsertEventMutation();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    mpEvent(MPEvents.FileLoaded, {
      modal: 'Add Actions modal',
      tags: ['ACTIONS'],
    });
    if (acceptedFiles?.length > 0) {
      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);
      setError('');
      setIsUploadSuccessful(false);
      setValidationErrors([]);
      setHasExistingEvents(false);
      parseCSVToJSON(selectedFile);
      setIsCsvDirty(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    },
  });

  const parseCSVToJSON = (file: File) => {
    const tryParse = (delimiter: string) =>
      new Promise<ParseResult<EventCSV>>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          dynamicTyping: false,
          skipEmptyLines: true,
          delimiter,
          complete: resolve,
          error: reject,
        });
      });

    (async () => {
      let result: ParseResult<EventCSV> | null = null;

      for (const delim of delimiters) {
        const parsed = await tryParse(delim);
        if (parsed.data && Object.keys(parsed.data[0] || {}).length > 2) {
          result = parsed;
          break;
        }
      }

      if (!result || !result.data || result.data.length === 0) {
        setValidationErrors([
          { rowIndex: 0, errors: [{ path: 'Data', message: 'No action data found in the file.' }] },
        ]);
        return;
      }

      if (result.data.length > 60) {
        setError('The number of actions exceeds the limit of 60.');
        return;
      }

      const mappedEvents: EventInput[] = result.data.map((el) => {
        const mapped = {
          id: el[EventCsvHeaders.ID],
          notes: el[EventCsvHeaders.Title] || '',
          ...(el[EventCsvHeaders.Owner] && { assignedTo: { text: el[EventCsvHeaders.Owner] } }),
          ...(el[EventCsvHeaders.DueDate] && {
            dueDate: convertDateToISO(el[EventCsvHeaders.DueDate]),
          }),
          completionStatus:
            el[EventCsvHeaders.CompletionStatus] === 'Completed'
              ? EventCompletionStatus.Done
              : EventCompletionStatus.Open,
          completedAt: el[EventCsvHeaders.DateCompleted] ? convertDateToISO(el[EventCsvHeaders.DateCompleted]) : null,
          ...(el[EventCsvHeaders.CompletedBy] && {
            completedBy: { text: el[EventCsvHeaders.CompletedBy] },
          }),
        };

        return mapped;
      });

      // Check if any CSV events have IDs that match existing events
      const hasExisting = mappedEvents.some((event) => event.id && eventIds.includes(event.id));
      setHasExistingEvents(hasExisting);

      const promises = mappedEvents.map((row, idx) =>
        validateRow({ row, rowIndex: idx + 1, schema: validationSchema, pathNames }),
      );
      const results = await Promise.all(promises);
      const errorLog = results.filter((el) => el?.errors?.length) as ValidationErrorItem[];

      if (errorLog.length) {
        setValidationErrors(errorLog);
        setIsUploadSuccessful(false);
        setReadyForUpload(false);
      } else {
        setValidEvents(mappedEvents);
        setValidationErrors([]);
        setError('');
        setReadyForUpload(true);
      }
    })();
  };

  const save = async () => {
    mpEvent(MPEvents.ButtonClick, {
      button: 'Upload',
      modal: 'Add Actions modal',
      tags: ['ACTIONS'],
    });

    setTotalEvents(validEvents.length);
    setReadyForUpload(false);

    try {
      const eventData: EventInput[] = validEvents.map((el) => {
        const { userAdd: assignedToUser } = selectUserOption({
          usersOptios,
          selectedUser: el.assignedTo,
        });

        const { userAdd: completedByUser } = selectUserOption({
          usersOptios,
          selectedUser: el.completedBy,
        });

        let completedAt = el.completedAt;
        if (el.completionStatus === EventCompletionStatus.Done && !completedAt) {
          completedAt = new Date().toISOString();
        }

        return {
          id: el.id,
          eventType: EventKind.Action,
          notes: el.notes,
          assignedTo: assignedToUser,
          dueDate: el.dueDate,
          objectType,
          objectId,
          completionStatus: el.completionStatus,
          completedAt: completedAt,
          completedBy: completedByUser,
        };
      });

      await upsertEvent({
        variables: { workspaceId, eventData },
      });

      queryCache.invalidateQueries();
      refetchQuery({ include: ['events'] });

      setIsUploadSuccessful(true);
      setIsCsvDirty(false);
      setReadyForUpload(false);
    } catch (error) {
      setError(`Error uploading actions: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setReadyForUpload(true);
    }
  };

  const resetUpload = () => {
    setIsUploadSuccessful(false);
    setValidationErrors([]);
    setError('');
    setFile(null);
    setTotalEvents(0);
    setReadyForUpload(false);
    setHasExistingEvents(false);
  };

  return (
    <DzoneWrap>
      {hasExistingEvents && <Alert severity="warning">This upload will modify existing action data.</Alert>}
      <a href={csvTemplateLink} download="actions_template.csv" style={{ textDecoration: 'none' }}>
        <GradientTextAction
          $underline
          weight={500}
          onClick={() =>
            mpEvent(MPEvents.DownloadCSVTemplate, {
              modal: 'Add Actions modal',
              tags: ['ACTIONS'],
            })
          }
        >
          Download CSV Template
        </GradientTextAction>
      </a>

      {!isUploadSuccessful && (
        <Dropzone {...getRootProps()}>
          <input {...getInputProps()} />
          <Poppins px={14}>
            {isDragActive ? 'Drop the file here ...' : 'Drag and drop a file here, or click to select file'}
          </Poppins>
        </Dropzone>
      )}

      {file && <Poppins px={14}>File selected: {file.name}</Poppins>}
      {!isUploadSuccessful && file && validationErrors.length === 0 && (
        <Poppins px={14}>Actions ready for upload: {validEvents.length}</Poppins>
      )}
      {isUploadSuccessful && <Poppins px={14}>File uploaded successfully!</Poppins>}
      {readyForUpload && !isUploadSuccessful && <Poppins px={14}>Validation successful, ready for upload</Poppins>}
      {validationErrors.length > 0 && (
        <ErrorLog>
          {validationErrors.map((error, index) => (
            <ErrorItem key={index}>
              <Poppins color="error" px={16} weight={600}>
                Error in row {error.rowIndex + 1}:
              </Poppins>
              {error.errors.map((e, idx) => (
                <Poppins px={14} color="error" css="display: block" key={idx}>{`${e.path}: ${e.message}`}</Poppins>
              ))}
            </ErrorItem>
          ))}
        </ErrorLog>
      )}
      {!isUploadSuccessful ? (
        <Button
          primary
          onClick={readyForUpload ? () => save() : resetUpload}
          css="width: 100%;"
          disabled={isButtonDisabled || !!validationErrors.length || loading}
        >
          {readyForUpload || loading ? 'Upload' : 'Select File'}
        </Button>
      ) : (
        <>
          <Button
            primary
            onClick={() => {
              resetUpload();
              setFile(null);
            }}
            css="width: 100%;"
          >
            Upload Another File
          </Button>
          <Button primary onClick={onClose} css="width: 100%;">
            Done
          </Button>
        </>
      )}
      {!file && (
        <>
          <Poppins px={14}>
            The valid values of Completion Status are listed in the drop down below. Blank is also valid for each.
          </Poppins>

          <div className="grid-list">
            <div>
              <InputLabel>Completion Status</InputLabel>
              <CSelect
                value={null}
                placeholder="Type"
                css="width: 100%;"
                menuPlacement="top"
                options={eventCompletionStatusOptions}
              />
            </div>
          </div>
        </>
      )}
      {error && <div className="error">{error}</div>}
    </DzoneWrap>
  );
};
